import React from 'react';
import {Navigate, Route, useSearchParams} from 'react-router-dom';
import {apiDocsPlugin, ApiExplorerPage} from '@backstage/plugin-api-docs';
import {CatalogEntityPage, CatalogIndexPage, catalogPlugin,} from '@backstage/plugin-catalog';
import {CatalogImportPage, catalogImportPlugin,} from '@backstage/plugin-catalog-import';
import {ScaffolderPage, scaffolderPlugin} from '@backstage/plugin-scaffolder';
import {orgPlugin} from '@backstage/plugin-org';
import {SearchPage} from '@backstage/plugin-search';
import {TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage,} from '@backstage/plugin-techdocs';
import {TechDocsAddons} from '@backstage/plugin-techdocs-react';
import {ReportIssue} from '@backstage/plugin-techdocs-module-addons-contrib';
import {UserSettingsPage} from '@backstage/plugin-user-settings';
import {apis, cloudidpOIDCAuthApiRef} from './apis';
import {entityPage} from './components/catalog/EntityPage';
import {searchPage} from './components/search/SearchPage';
import {Root} from './components/Root';

import {AlertDisplay, OAuthRequestDialog, SignInPage,} from '@backstage/core-components';
import {createApp} from '@backstage/app-defaults';
import {AppRouter, FlatRoutes} from '@backstage/core-app-api';
import {CatalogGraphPage} from '@backstage/plugin-catalog-graph';
import {RequirePermission} from '@backstage/plugin-permission-react';
import {catalogEntityCreatePermission} from '@backstage/plugin-catalog-common/alpha';
import {DevToolsPage} from '@backstage/plugin-devtools';
import {CatalogUnprocessedEntitiesPage} from '@backstage/plugin-catalog-unprocessed-entities';
import {customDevToolsPage} from './components/devtools/CustomDevToolsPage';
import {simpleIconsColor} from '@dweber019/backstage-plugin-simple-icons';
import {configApiRef, identityApiRef, useApi} from '@backstage/core-plugin-api';
import Cookies from 'universal-cookie';

const app = createApp({
    apis, bindRoutes({bind}) {
        bind(catalogPlugin.externalRoutes, {
            createComponent: scaffolderPlugin.routes.root,
            viewTechDoc: techdocsPlugin.routes.docRoot,
            createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
        });
        bind(apiDocsPlugin.externalRoutes, {
            registerApi: catalogImportPlugin.routes.importPage,
        });
        bind(scaffolderPlugin.externalRoutes, {
            registerComponent: catalogImportPlugin.routes.importPage, viewTechDoc: techdocsPlugin.routes.docRoot,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    }, components: {
        SignInPage: props => {
            const identityApi = useApi(identityApiRef);
            const [searchParams] = useSearchParams();
            identityApi.getCredentials()
                .then(result => {
                    const cookies = new Cookies();
                    cookies.set('cloudidp-access-token', result.token, {
                        path: '/',
                        sameSite: 'lax'
                    });
                    const navigateParam = searchParams.get("navigate");
                    if (navigateParam){
                        window.location.href = navigateParam;
                    }
                });

            const configApi = useApi(configApiRef);
            if (configApi.getString('auth.environment') === 'development') {
                return (<SignInPage
                        {...props} auto providers={['guest', {
                        id: 'cloudidp',
                        title: 'CloudIDP',
                        message: 'Sign in using CloudIDP',
                        apiRef: cloudidpOIDCAuthApiRef,
                    }]}
                    />);
            }
            return <SignInPage {...props} auto provider={
                {
                    id: 'cloudidp',
                    title: 'CloudIDP',
                    message: 'Sign in using CloudIDP',
                    apiRef: cloudidpOIDCAuthApiRef,
                }
            }
            />
        },
    }, icons: simpleIconsColor,
});

const routes = (<FlatRoutes>
        <Route path="/" element={<Navigate to="catalog"/>}/>
        <Route path="/catalog" element={<CatalogIndexPage/>}/>
        <Route
            path="/catalog/:namespace/:kind/:name"
            element={<CatalogEntityPage/>}
        >
            {entityPage}
        </Route>
        <Route path="/docs" element={<TechDocsIndexPage/>}/>
        <Route
            path="/docs/:namespace/:kind/:name/*"
            element={<TechDocsReaderPage/>}
        >
            <TechDocsAddons>
                <ReportIssue/>
            </TechDocsAddons>
        </Route>
        <Route path="/create" element={<ScaffolderPage/>}/>
        <Route path="/api-docs" element={<ApiExplorerPage/>}/>
        <Route
            path="/catalog-import"
            element={<RequirePermission permission={catalogEntityCreatePermission}>
                <CatalogImportPage/>
            </RequirePermission>}
        />
        <Route path="/search" element={<SearchPage/>}>
            {searchPage}
        </Route>
        <Route path="/settings" element={<UserSettingsPage/>}/>
        <Route path="/catalog-graph" element={<CatalogGraphPage/>}/>
        <Route path="/devtools" element={<DevToolsPage/>}>
            {customDevToolsPage}
        </Route>
        <Route path="/catalog-unprocessed-entities" element={<CatalogUnprocessedEntitiesPage/>}/>
    </FlatRoutes>);

export default app.createRoot(<>
    <AlertDisplay/>
    <OAuthRequestDialog/>
    <AppRouter>
        <Root>{routes}</Root>
    </AppRouter>
</>,);
